/**
 * Projection du journal vers l'état de l'élection, et calcul des résultats.
 *
 * L'état n'est jamais modifié directement : il est **reconstruit** à partir du
 * journal. C'est ce qui rend la reprise après panne sûre — on ne synchronise
 * pas un état, on fusionne des journaux.
 */

import { fingerprint } from './hash.js';
import { mergeOrder } from './journal.js';
import { selectSeats, tallyRound } from './rules.js';
import {
  DEFAULT_RULES,
  type BallotRecord,
  type Designation,
  type ElectionConfig,
  type Item,
  type JournalEvent,
  type MandateDecision,
  type OverrideAct,
  type Person,
  type Phase,
  type RoundResult,
  type Seat,
  type TiebreakDecision,
} from './types.js';

export interface Declaration {
  readonly titulaireId: string;
  readonly suppleantId?: string;
  readonly itemId: string;
  readonly ts: number;
}

export interface CodesInfo {
  readonly count: number;
  readonly hashes: readonly string[];
  readonly teacherHash: string;
}

export interface ElectionState {
  electionId: string;
  config: ElectionConfig | null;
  phase: Phase;
  /** Sel du scrutin : les empreintes de jetons ne sont pas réutilisables ailleurs. */
  salt: string;
  persons: Person[];
  codes: CodesInfo | null;
  declarations: Declaration[];
  removedItemIds: string[];
  ballots: BallotRecord[];
  tiebreaks: TiebreakDecision[];
  mandates: MandateDecision[];
  designations: Designation[];
  overrides: OverrideAct[];
  validations: OverrideAct[];
  /** Liste des items en lice au second tour, validée par l'enseignant. */
  tour2ItemIds: string[] | null;
  lastCounter: Record<string, number>;
  lastHash: Record<string, string>;
  events: JournalEvent[];
  incidents: string[];
}

export function emptyState(electionId: string): ElectionState {
  return {
    electionId,
    config: null,
    phase: 'BROUILLON',
    salt: '',
    persons: [],
    codes: null,
    declarations: [],
    removedItemIds: [],
    ballots: [],
    tiebreaks: [],
    mandates: [],
    designations: [],
    overrides: [],
    validations: [],
    tour2ItemIds: null,
    lastCounter: {},
    lastHash: {},
    events: [],
    incidents: [],
  };
}

/** Identifiant d'item déterministe : rejouer le journal produit les mêmes items. */
export function itemIdFor(titulaireId: string, suppleantId?: string): string {
  return suppleantId ? `bin_${titulaireId}_${suppleantId}` : `bin_${titulaireId}`;
}

function applyEvent(state: ElectionState, event: JournalEvent): void {
  switch (event.type) {
    case 'election.created': {
      const payload = event.payload as { config: ElectionConfig; salt: string };
      state.config = payload.config;
      state.salt = payload.salt;
      state.phase = 'BROUILLON';
      break;
    }
    case 'roster.imported': {
      state.persons = (event.payload as { persons: Person[] }).persons;
      break;
    }
    case 'codes.generated': {
      const payload = event.payload as CodesInfo;
      state.codes = { count: payload.count, hashes: [...payload.hashes], teacherHash: payload.teacherHash };
      break;
    }
    case 'candidate.declared': {
      const payload = event.payload as { titulaireId: string; suppleantId?: string };
      const declaration: Declaration = payload.suppleantId
        ? { titulaireId: payload.titulaireId, suppleantId: payload.suppleantId, itemId: itemIdFor(payload.titulaireId, payload.suppleantId), ts: event.ts }
        : { titulaireId: payload.titulaireId, itemId: itemIdFor(payload.titulaireId), ts: event.ts };
      state.declarations.push(declaration);
      break;
    }
    case 'candidate.removed': {
      state.removedItemIds.push((event.payload as { itemId: string }).itemId);
      break;
    }
    case 'phase.changed': {
      state.phase = (event.payload as { to: Phase }).to;
      break;
    }
    case 'tour2.validated': {
      state.tour2ItemIds = [...(event.payload as { itemIds: string[] }).itemIds];
      break;
    }
    case 'ballot.cast': {
      const payload = event.payload as { tokenHash: string; choices: string[]; blank: boolean; round: 1 | 2 };
      state.ballots.push({
        tokenHash: payload.tokenHash,
        choices: [...payload.choices],
        blank: payload.blank,
        round: payload.round,
        ts: event.ts,
      });
      break;
    }
    case 'tiebreak.decided': {
      state.tiebreaks.push(event.payload as TiebreakDecision);
      break;
    }
    case 'mandate.decided': {
      state.mandates.push(event.payload as MandateDecision);
      break;
    }
    case 'suppleant.designated': {
      state.designations.push(event.payload as Designation);
      break;
    }
    case 'public.state': {
      // Publication destinée aux élèves : elle n'entre pas dans l'état de
      // l'élection. Sans ce cas explicite, elle serait comptée comme incident.
      break;
    }
    case 'result.validated': {
      state.validations.push({ ...(event.payload as Omit<OverrideAct, 'kind' | 'ts'>), kind: 'validation', ts: event.ts });
      break;
    }
    case 'result.overridden': {
      state.overrides.push({ ...(event.payload as Omit<OverrideAct, 'kind' | 'ts'>), kind: 'modification', ts: event.ts });
      break;
    }
    default: {
      state.incidents.push(`événement de type inconnu ignoré : ${String((event as { type: string }).type)}`);
    }
  }

  state.lastCounter[event.origin] = Math.max(state.lastCounter[event.origin] ?? 0, event.counter);
  state.lastHash[event.origin] = event.hash;
  state.events.push(event);
}

/** Reconstruit l'état complet à partir du journal fusionné. */
export function project(events: readonly JournalEvent[], electionId: string): ElectionState {
  const state = emptyState(electionId);
  for (const event of mergeOrder(events)) {
    if (event.electionId !== electionId) continue;
    applyEvent(state, event);
  }
  return state;
}

export function personById(state: ElectionState, personId: string): Person | undefined {
  return state.persons.find((person) => person.id === personId);
}

export function personLabel(state: ElectionState, personId: string): string {
  return personById(state, personId)?.label ?? '?';
}

/**
 * Items soumis au vote.
 *
 * Les binômes validés structurent le scrutin ; en mode « liste de classe »,
 * toute personne ne figurant sur aucun binôme devient votable **seule**
 * (O7 : aucun recouvrement, donc aucun score éclaté).
 */
export function itemsOf(state: ElectionState): Item[] {
  const rules = state.config?.rules ?? DEFAULT_RULES;
  const retires = new Set(state.removedItemIds);

  const binomes: Item[] = state.declarations
    .filter((declaration) => !retires.has(declaration.itemId))
    .map((declaration) => {
      const titulaire = personLabel(state, declaration.titulaireId);
      const suppleant = declaration.suppleantId ? personLabel(state, declaration.suppleantId) : null;
      const base = {
        id: declaration.itemId,
        kind: 'binome' as const,
        titulaireId: declaration.titulaireId,
        label: suppleant ? `${titulaire} + ${suppleant}` : titulaire,
      };
      return declaration.suppleantId ? { ...base, suppleantId: declaration.suppleantId } : base;
    });

  if (rules.eligibilite !== 'liste_classe') return binomes;

  const occupes = new Set<string>();
  for (const declaration of state.declarations) {
    if (retires.has(declaration.itemId)) continue;
    occupes.add(declaration.titulaireId);
    if (declaration.suppleantId) occupes.add(declaration.suppleantId);
  }

  const individus: Item[] = state.persons
    .filter((person) => !occupes.has(person.id))
    .map((person) => ({
      id: `ind_${person.id}`,
      kind: 'individu' as const,
      titulaireId: person.id,
      label: person.label,
    }));

  return [...binomes, ...individus];
}

export interface Ambiguity {
  readonly round: 1 | 2;
  readonly itemIds: readonly string[];
}

export interface Computation {
  readonly items: Item[];
  readonly round1: RoundResult;
  readonly round2: RoundResult | null;
  readonly elusTour1: readonly string[];
  readonly elusTour2: readonly string[];
  /** Égalité non tranchée : un départage manuel est obligatoire. */
  readonly ambigu: Ambiguity | null;
  /**
   * Nombre de sièges de titulaire que le second tour doit pourvoir.
   * Vaut 0 lorsque le premier tour a suffi — c'est la seule condition qui
   * dispense de second tour.
   */
  readonly seatsRestants: number;
  readonly tour2Requis: boolean;
  readonly itemsEnLice2: readonly string[];
  /** Sièges issus du seul calcul, avant tout acte souverain. */
  readonly siegesCalcules: readonly Seat[];
  /** Individus élus titulaires dont le suppléant reste à désigner (O8). */
  readonly designationsAttendues: readonly { titulaireId: string; label: string }[];
  readonly empreinte: string;
}

function resolveTiebreaks(
  round: 1 | 2,
  elus: string[],
  tied: readonly string[] | null,
  capacite: number,
  decisions: readonly TiebreakDecision[],
): { elus: string[]; tied: string[] | null } {
  let restants = tied ? [...tied] : [];
  if (restants.length === 0) return { elus, tied: null };

  const dejaUtilisees = new Set<string>();
  let progres = true;
  while (progres && restants.length > 0 && elus.length < capacite) {
    progres = false;
    for (const decision of decisions) {
      if (decision.round !== round) continue;
      if (dejaUtilisees.has(decision.chosenItemId)) continue;
      if (!restants.includes(decision.chosenItemId)) continue;
      dejaUtilisees.add(decision.chosenItemId);
      elus.push(decision.chosenItemId);
      restants = restants.filter((id) => id !== decision.chosenItemId);
      progres = true;
      break;
    }
  }

  return { elus, tied: restants.length > 0 ? restants : null };
}

/**
 * Calcule intégralement le résultat : deux tours, égalités signalées,
 * sièges attribués, désignations restantes. Aucune décision souveraine n'est
 * appliquée ici : le calcul reste toujours visible à côté du résultat retenu.
 */
export function compute(state: ElectionState): Computation {
  const rules = state.config?.rules ?? DEFAULT_RULES;
  const items = itemsOf(state);
  const parId = new Map(items.map((item) => [item.id, item]));
  const inscrits = state.codes?.count ?? state.persons.length;
  const refuses = new Set(state.mandates.filter((m) => !m.accepted).map((m) => m.personId));

  // Un titulaire qui refuse son mandat est écarté : le siège passe au suivant
  // du classement (O3).
  const eligible = (itemId: string): boolean => {
    const item = parId.get(itemId);
    return item ? !refuses.has(item.titulaireId) : false;
  };

  const round1 = tallyRound({
    round: 1,
    items,
    ballots: state.ballots.filter((ballot) => ballot.round === 1),
    votesPerElector: rules.voixTour1,
    absoluteMajority: true,
    inscrits,
  });

  const sel1 = selectSeats(round1.classement.filter((entry) => eligible(entry.itemId)), rules.titulaires, {
    absoluteMajority: true,
    seuil: round1.seuil,
  });

  let ambigu: Ambiguity | null = sel1.tied.length > 0 ? { round: 1, itemIds: [...sel1.tied] } : null;
  let elusTour1 = [...sel1.elected];
  {
    const resolu = resolveTiebreaks(1, elusTour1, ambigu ? ambigu.itemIds : null, rules.titulaires, state.tiebreaks);
    elusTour1 = resolu.elus;
    ambigu = resolu.tied ? { round: 1, itemIds: resolu.tied } : null;
  }

  const seatsRestants = Math.max(0, rules.titulaires - elusTour1.length);
  let round2: RoundResult | null = null;
  let elusTour2: string[] = [];
  let itemsEnLice2: string[] = [];

  // Une égalité du premier tour non tranchée suspend la suite : on n'ouvre pas
  // le second tour tant que l'attribution des sièges du premier n'est pas fixée.
  if (seatsRestants > 0 && ambigu === null) {
    const nonElus = items.filter((item) => !elusTour1.includes(item.id));
    itemsEnLice2 = state.tour2ItemIds
      ? nonElus.filter((item) => (state.tour2ItemIds as string[]).includes(item.id)).map((item) => item.id)
      : nonElus.map((item) => item.id);

    const enLice = items.filter((item) => itemsEnLice2.includes(item.id));
    round2 = tallyRound({
      round: 2,
      items: enLice,
      ballots: state.ballots.filter((ballot) => ballot.round === 2),
      votesPerElector: seatsRestants,
      absoluteMajority: false,
      inscrits,
    });

    // Un second tour a toujours lieu dès qu'un siège reste à pourvoir, même si
    // un seul item demeure en lice : le scrutin est un vrai vote, il n'existe
    // aucune élection automatique. Sans bulletin valide, aucun siège ne peut
    // être attribué par le calcul.
    if (round2.valides > 0) {
      const sel2 = selectSeats(round2.classement.filter((entry) => eligible(entry.itemId)), seatsRestants, {
        absoluteMajority: false,
        seuil: null,
      });

      if (sel2.tied.length > 0) ambigu = { round: 2, itemIds: [...sel2.tied] };
      const resolu = resolveTiebreaks(2, [...sel2.elected], sel2.tied.length > 0 ? [...sel2.tied] : null, seatsRestants, state.tiebreaks);
      elusTour2 = resolu.elus;
      if (resolu.tied) ambigu = { round: 2, itemIds: resolu.tied };
      else if (ambigu?.round === 2) ambigu = null;
    }
  }

  const siegesCalcules: Seat[] = [];
  const designationsAttendues: { titulaireId: string; label: string }[] = [];

  for (const itemId of [...elusTour1, ...elusTour2]) {
    const item = parId.get(itemId);
    if (!item) continue;

    siegesCalcules.push({
      role: 'titulaire',
      personId: item.titulaireId,
      itemId,
      via: 'elu',
    });

    if (item.kind === 'binome') {
      if (item.suppleantId && !refuses.has(item.suppleantId)) {
        siegesCalcules.push({ role: 'suppleant', personId: item.suppleantId, itemId, via: 'elu' });
      }
      continue;
    }

    // Individu élu titulaire : son suppléant se désigne après le dépouillement.
    const designation = state.designations.find((entry) => entry.titulaireId === item.titulaireId);
    if (designation) {
      siegesCalcules.push({ role: 'suppleant', personId: designation.suppleantId, via: 'designe' });
    } else {
      designationsAttendues.push({ titulaireId: item.titulaireId, label: item.label });
    }
  }

  const empreinte = fingerprint({
    round1: { valides: round1.valides, blancs: round1.blancs, nuls: round1.nuls, classement: round1.classement },
    round2: round2 ? { valides: round2.valides, blancs: round2.blancs, nuls: round2.nuls, classement: round2.classement } : null,
    siegesCalcules,
  });

  return {
    items,
    round1,
    round2,
    elusTour1,
    elusTour2,
    ambigu,
    seatsRestants,
    tour2Requis: seatsRestants > 0,
    itemsEnLice2,
    siegesCalcules,
    designationsAttendues,
    empreinte,
  };
}

/** Dernier acte souverain enregistré, ou `null`. */
export function dernierActeSouverain(state: ElectionState): OverrideAct | null {
  const tous = [...state.validations, ...state.overrides].sort((a, b) => a.ts - b.ts);
  return tous.length > 0 ? (tous[tous.length - 1] as OverrideAct) : null;
}

/**
 * Sièges retenus : le calcul, sauf si l'enseignant a usé de son pouvoir de
 * modification (§6.8). Le calcul reste disponible dans `computation`.
 */
export function retainedSeats(state: ElectionState, computation: Computation): Seat[] {
  const derniere = [...state.overrides].sort((a, b) => a.ts - b.ts).pop();
  if (derniere && derniere.kind === 'modification' && derniere.seats.length > 0) {
    return [...derniere.seats];
  }
  return [...computation.siegesCalcules];
}
